/**
 * Master v5 Prompt 28 — the app's single QueryClient instance, exported
 * so non-component modules (rehearsalRoomAuth.js's logout, the
 * View-as-Client toggle) can clear/invalidate its cache directly
 * without threading it through every component tree. Previously this
 * was created inline inside index.js with no way to reach it from
 * outside a component — cache was NEVER cleared on logout or on
 * entering/exiting View as Client, so a second person logging in on
 * the same browser (or a coach toggling View as Client) could
 * momentarily see the previous user's/mode's cached data.
 *
 * Also adds a global expired-session handler: previously only ONE page
 * (RehearsalComparisonPage) redirected to login on a 401 — every other
 * query/mutation just surfaced a generic error state, leaving the user
 * stuck on a broken page after their session expired. `QueryCache`/
 * `MutationCache` `onError` (the v5-correct place for this — the old
 * `defaultOptions.queries.onError` was removed in v5) catches a 401
 * from ANY query or mutation across the whole app exactly once and
 * hard-redirects to login (a full reload also guarantees no stale
 * in-memory state lingers).
 */
import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";

function handleExpiredSession(error) {
  if (error?.status !== 401) return;
  const { pathname } = window.location;
  if (!pathname.startsWith("/rehearsal-room") || pathname === "/rehearsal-room/login") return;
  // Mirrors clearTokens()'s exact key names in rehearsalRoomAuth.js —
  // duplicated (not imported) deliberately to avoid a circular import
  // (rehearsalRoomAuth.js imports `queryClient` from this same file).
  localStorage.removeItem("rr_access_token");
  localStorage.removeItem("rr_refresh_token");
  window.location.href = "/rehearsal-room/login";
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleExpiredSession }),
  mutationCache: new MutationCache({ onError: handleExpiredSession }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});
